// frontend/src/components/Forms/VolunteerForm.tsx
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';

interface VolunteerFormData {
  name: string;
  email: string;
  phone: string;
  skills: string;
  preferred_program: string;
  message: string;
}

const VolunteerForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<VolunteerFormData>();

  const onSubmit = async (data: VolunteerFormData) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await axios.post(
        'http://localhost/iga/backend/api/volunteers',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 201) {
        setSubmitMessage('Thank you for your application! We will contact you soon.');
        reset();
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitMessage('Sorry, there was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        <div className="card shadow">
          <div className="card-body p-4 p-md-5">
            <h3 className="card-title text-center mb-4">Join Our Volunteer Team</h3>
            
            {submitMessage && (
              <div className={`alert ${submitMessage.includes('Thank you') ? 'alert-success' : 'alert-danger'}`}>
                {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="phone"
                    {...register('phone')}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="preferred_program" className="form-label">Preferred Program</label>
                  <select
                    className="form-select"
                    id="preferred_program"
                    {...register('preferred_program')}
                  >
                    <option value="">Select a program</option>
                    <option value="food_security">Food Security</option>
                    <option value="climate_action">Climate Action</option>
                    <option value="youth_empowerment">Youth Empowerment</option>
                    <option value="women_empowerment">Women Empowerment</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="skills" className="form-label">Skills & Experience</label>
                <textarea
                  className="form-control"
                  id="skills"
                  rows={3}
                  placeholder="Tell us about your skills, background, and experience..."
                  {...register('skills')}
                ></textarea>
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="form-label">Why do you want to volunteer with us?</label>
                <textarea
                  className="form-control"
                  id="message"
                  rows={4}
                  placeholder="Share your motivation and what you hope to achieve..."
                  {...register('message')}
                ></textarea>
              </div>

              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolunteerForm;